import React, { useState } from 'react';
import { api } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { BottomSheet } from './mobile/BottomSheet';
import { Button } from './ui/Button';
import { X, Check, Sparkle } from './ui/Icons';

type Plan = 'solo' | 'business' | 'pro';

interface PlanDef {
  key: Plan | 'free';
  name: string;
  price: string;
  tagline: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
}

const PLANS: PlanDef[] = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    tagline: '20 receipts lifetime',
    features: ['Core extraction', 'Manual posting', 'Email ingest'],
  },
  {
    key: 'solo',
    name: 'Solo',
    price: '$9.99/mo',
    tagline: '75 receipts / month',
    features: ['Core features', 'Auto-post to your books', 'Email forwarding'],
  },
  {
    key: 'business',
    name: 'Business',
    price: '$19.99/mo',
    tagline: '300 receipts / month',
    features: ['Everything in Solo', 'Gmail auto-sync', 'Priority support'],
    highlighted: true,
    badge: 'Most popular',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$39.99/mo',
    tagline: '1,000 receipts / month',
    features: ['Everything in Business', 'Multiple company files', 'Outlook sync (soon)'],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  reason?: string;
}

interface PromoState {
  code: string;
  result?: { valid: boolean; discount?: string; id?: string };
  checking?: boolean;
}

function PlanCard({
  plan,
  highlighted,
  onUpgrade,
  upgrading,
  promo,
  setPromo,
  onApplyPromo,
}: {
  plan: PlanDef;
  highlighted: boolean;
  onUpgrade: (p: Plan, code?: string) => void;
  upgrading: boolean;
  promo: PromoState;
  setPromo: (s: PromoState) => void;
  onApplyPromo: () => void;
}) {
  const isFree = plan.key === 'free';
  const paid = plan.key as Plan;

  return (
    <div
      style={{
        position: 'relative',
        background: highlighted ? T.navy : '#FFFFFF',
        color: highlighted ? T.cream : T.graphite,
        border: `1px solid ${highlighted ? T.navy : T.paperLine}`,
        borderRadius: 14,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: highlighted ? '0 8px 24px rgba(10,20,40,0.18)' : 'none',
      }}
    >
      {plan.badge && (
        <span
          style={{
            position: 'absolute',
            top: -10,
            left: 16,
            background: T.sage,
            color: T.navy,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            borderRadius: 100,
          }}
        >
          {plan.badge}
        </span>
      )}

      <div>
        <div
          style={{
            ...sx.label,
            color: highlighted ? T.creamDim : T.graphiteDim,
            marginBottom: 6,
          }}
        >
          {plan.name}
        </div>
        <div
          style={{
            ...sx.serif,
            fontSize: 24,
            color: highlighted ? T.cream : T.graphite,
            marginBottom: 4,
          }}
        >
          {plan.price}
        </div>
        <div
          style={{
            fontSize: 12,
            color: highlighted ? T.creamDim : T.graphiteDim,
          }}
        >
          {plan.tagline}
        </div>
      </div>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          flex: 1,
        }}
      >
        {plan.features.map((f) => (
          <li
            key={f}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              fontSize: 13,
              color: highlighted ? T.cream : T.graphite,
              lineHeight: 1.4,
            }}
          >
            <span style={{ flexShrink: 0, marginTop: 2 }}>
              <Check size={13} color={highlighted ? T.sage : T.sageDeep} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isFree ? (
        <div
          style={{
            fontSize: 12,
            color: T.graphiteDim,
            fontStyle: 'italic',
            padding: '8px 0',
          }}
        >
          Current plan
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button
            variant={highlighted ? 'sage' : 'navy'}
            size="default"
            onClick={() => onUpgrade(paid, promo.result?.valid ? promo.code : undefined)}
            disabled={upgrading}
            style={{ width: '100%' }}
          >
            <Sparkle size={13} />
            {upgrading ? 'Loading…' : `Upgrade to ${plan.name}`}
          </Button>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={promo.code}
                onChange={(e) =>
                  setPromo({ code: e.target.value, result: undefined })
                }
                placeholder="Promo code"
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  fontSize: 12,
                  fontFamily: "'Geist Mono', monospace",
                  background: highlighted ? T.navy2 : '#FFFFFF',
                  color: highlighted ? T.cream : T.graphite,
                  border: `1px solid ${highlighted ? T.navyLine2 : T.paperLine}`,
                  borderRadius: 6,
                  outline: 'none',
                }}
              />
              <button
                onClick={onApplyPromo}
                disabled={!promo.code.trim() || promo.checking}
                style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: "'Geist', sans-serif",
                  background: 'transparent',
                  color: highlighted ? T.cream : T.graphite,
                  border: `1px solid ${highlighted ? T.navyLine2 : T.paperLine}`,
                  borderRadius: 6,
                  cursor: promo.code.trim() ? 'pointer' : 'not-allowed',
                  opacity: promo.code.trim() ? 1 : 0.5,
                }}
              >
                {promo.checking ? '…' : 'Apply'}
              </button>
            </div>
            {promo.result && (
              <div
                style={{
                  fontSize: 11,
                  color: promo.result.valid
                    ? (highlighted ? T.sage : T.sageDarker)
                    : T.fail,
                  fontFamily: "'Geist Mono', monospace",
                }}
              >
                {promo.result.valid
                  ? `✓ ${promo.result.discount ?? 'Applied'}`
                  : '✗ Invalid code'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function UpgradeModal({ open, onClose, reason }: Props) {
  const isMobile = useIsMobile();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promoByPlan, setPromoByPlan] = useState<Record<Plan, PromoState>>({
    solo: { code: '' },
    business: { code: '' },
    pro: { code: '' },
  });

  if (!open) return null;

  async function handleUpgrade(plan: Plan, code?: string) {
    setUpgrading(true);
    setError(null);
    try {
      const res = await api.billingCheckout(plan, code);
      window.location.href = res.url;
    } catch (err) {
      setError((err as Error).message);
      setUpgrading(false);
    }
  }

  async function handleApplyPromo(plan: Plan) {
    const current = promoByPlan[plan];
    const code = current.code.trim();
    if (!code) return;
    setPromoByPlan((p) => ({ ...p, [plan]: { ...current, checking: true } }));
    try {
      const res = await api.billingPromoPreview(code);
      setPromoByPlan((p) => ({
        ...p,
        [plan]: { code, result: res, checking: false },
      }));
    } catch {
      setPromoByPlan((p) => ({
        ...p,
        [plan]: { code, result: { valid: false }, checking: false },
      }));
    }
  }

  function setPromo(plan: Plan, state: PromoState) {
    setPromoByPlan((p) => ({ ...p, [plan]: state }));
  }

  // Mobile order: Business first, then Solo, Pro, Free.
  const mobileOrder: PlanDef[] = [
    PLANS.find((p) => p.key === 'business')!,
    PLANS.find((p) => p.key === 'solo')!,
    PLANS.find((p) => p.key === 'pro')!,
    PLANS.find((p) => p.key === 'free')!,
  ];

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ ...sx.label, color: T.graphiteDim, marginBottom: 6 }}>
          Choose a plan
        </div>
        <h2 style={{ ...sx.serif, fontSize: 26, margin: 0, color: T.graphite }}>
          Upgrade receiptrec
        </h2>
        {reason && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 12px',
              background: T.warnBg,
              color: T.warn,
              border: `1px solid ${T.warn}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              display: 'inline-block',
            }}
          >
            {reason}
          </div>
        )}
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          color: T.graphiteDim,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <X size={18} />
      </button>
    </div>
  );

  const errorBanner = error && (
    <div
      style={{
        marginTop: 12,
        padding: '10px 14px',
        background: T.failBg,
        color: T.fail,
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {error}
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet open={open} onClose={onClose} maxHeightPct={92}>
        <div
          style={{
            padding: '4px 20px 24px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {header}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mobileOrder.map((p) => {
              const highlighted = p.key === 'business';
              const plan = p.key === 'free' ? null : (p.key as Plan);
              return (
                <PlanCard
                  key={p.key}
                  plan={p}
                  highlighted={highlighted}
                  onUpgrade={handleUpgrade}
                  upgrading={upgrading}
                  promo={plan ? promoByPlan[plan] : { code: '' }}
                  setPromo={(s) => plan && setPromo(plan, s)}
                  onApplyPromo={() => plan && handleApplyPromo(plan)}
                />
              );
            })}
          </div>
          {errorBanner}
          <div style={{ height: 'env(safe-area-inset-bottom, 12px)' }} />
        </div>
      </BottomSheet>
    );
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(10,20,40,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.paper,
          width: '100%',
          maxWidth: 960,
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 18,
          padding: 28,
          boxShadow: '0 24px 64px rgba(10,20,40,0.35)',
        }}
      >
        {header}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 14,
            marginTop: 4,
          }}
        >
          {PLANS.map((p) => {
            const highlighted = p.key === 'business';
            const plan = p.key === 'free' ? null : (p.key as Plan);
            return (
              <PlanCard
                key={p.key}
                plan={p}
                highlighted={highlighted}
                onUpgrade={handleUpgrade}
                upgrading={upgrading}
                promo={plan ? promoByPlan[plan] : { code: '' }}
                setPromo={(s) => plan && setPromo(plan, s)}
                onApplyPromo={() => plan && handleApplyPromo(plan)}
              />
            );
          })}
        </div>
        {errorBanner}
        <div
          style={{
            marginTop: 18,
            fontSize: 11,
            color: T.graphiteDim,
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          Cancel any time from the billing portal.
        </div>
      </div>
    </div>
  );
}
